define('drops/audioPlayer/audioPlayerCustom',[
    "jquery", 
    "underscore", 
    "./audioPlayerBase"
], function ($, _, AudioPlayerBase) {
    "use strict";

    function AudioPlayerCustom(drop) {
        AudioPlayerBase.call(this, drop);

        this.isUserSliding = false;
        this.isUserAdjustingVolume = false;
        this.rafId = null;
        this.controlDropCache = null;
    }

    AudioPlayerCustom.prototype = Object.create(AudioPlayerBase.prototype);
    AudioPlayerCustom.prototype.constructor = AudioPlayerCustom;

    AudioPlayerCustom.prototype.createAudioElement = function () {
        this.$audio = $("<audio />").css({ display: "none" });
        this.audioElement = this.$audio.get(0);
        this.drop.$element.append(this.$audio);

        return this.audioElement;
    };

    AudioPlayerCustom.prototype.cacheControlDrops = function () {
        var roles = [
            "audio_play",
            "audio_pause",
            "audio_seek_bar",
            "audio_current_time",
            "audio_duration",
            "audio_speed",
            "audio_mute",
            "audio_unmute",
            "audio_skip_back",
            "audio_skip_forward",
            "audio_volume_slider",
        ];
        var self = this;
        this.controlDropCache = {};
        _.each(roles, function (role) {
            self.controlDropCache[role] = self.drop.findChildDropByRole(role);
        });
    };

    AudioPlayerCustom.prototype.getControlDrop = function (role) {
        if (!this.controlDropCache) this.cacheControlDrops();

        return this.controlDropCache[role] || null;
    };

    AudioPlayerCustom.prototype.getNoUiSlider = function (role) {
        var drop = this.getControlDrop(role);
        return drop && drop.$slider && drop.$slider[0] && drop.$slider[0].noUiSlider
            ? drop.$slider[0].noUiSlider
            : null;
    };

    AudioPlayerCustom.prototype.initialize = function () {
        var defaultRate = this.getDefaultPlaybackRate();

        // Headless audio element (no native controls)
        if (!this.$audio) {
            this.createAudioElement();
        }

        if (this.audioElement) {
            this.audioElement.playbackRate = defaultRate;
        }

        // Configure audio element attributes
        var url = this.drop.getAudioUrl();

        this.$audio
            .prop("autoplay", !!(this.drop.data.autoPlay && url))
            .prop("loop", !!this.drop.data.loop)
            .prop("muted", !!this.drop.data.muted)
            .attr("preload", this.drop.data.preload || "metadata");

        if (url && this.$audio.attr("src") !== url) {
            this.$audio.attr("src", url);
        }

        // Bind central audio DOM events (play, pause, ended, timeupdate, durationchange)
        this.bindAudioEvents();

        // Locate and bind child control drops
        this.bindChildControls();

        // Initialize UI visibility and initial state
        var initialIsPlaying = this.audioElement ? !this.audioElement.paused : false;
        this.updatePlayPauseUI(initialIsPlaying);
        this.updateMuteUI();
        this.updateSpeedUI(defaultRate);

        // Initialize Current Time, Duration, and Volume labels/sliders on initial load
        var initialSeek =
            this.drop && typeof this.drop.getInitialSkipToTimestampSeconds === "function"
                ? this.drop.getInitialSkipToTimestampSeconds()
                : 0;
        var initialCurr = initialSeek > 0 ? initialSeek : this.audioElement ? this.audioElement.currentTime : 0;
        var initialDur = this.audioElement ? this.audioElement.duration : 0;

        var isMuted = this.audioElement
            ? this.audioElement.muted || this.audioElement.volume === 0
            : !!this.drop.data.muted;

        var initialVolume = isMuted ? 0 : this.audioElement ? Math.round(this.audioElement.volume * 100) : 100;

        if (this.audioElement && this.audioElement.volume > 0) {
            this.lastUnmutedVolume = this.audioElement.volume;
        }

        this.updateCurrentTimeUI(initialCurr);
        this.updateSeekBarValue(initialCurr);
        this.updateDurationUI(initialDur);
        this.updateSeekBarMax(initialDur);
        this.updateVolumeSliderValue(initialVolume);
    };

    // UI Lifecycle Hook Implementations
    AudioPlayerCustom.prototype.onPlay = function () {
        this.updatePlayPauseUI(true);
        this.startSeekAnimation();
    };

    AudioPlayerCustom.prototype.onPause = function () {
        this.updatePlayPauseUI(false);
        this.stopSeekAnimation();
    };

    AudioPlayerCustom.prototype.onEnded = function () {
        this.resetPlayerState(false);
    };

    AudioPlayerCustom.prototype.onTimeUpdate = function (curr) {
        this.updateCurrentTimeUI(curr);
    };

    AudioPlayerCustom.prototype.onDurationChange = function (dur) {
        this.updateDurationUI(dur);
        this.updateSeekBarMax(dur);
    };

    AudioPlayerCustom.prototype.onVolumeChange = function (vol) {
        this.updateMuteUI();
        if (!this.isUserAdjustingVolume) {
            this.updateVolumeSliderValue(vol);
        }
    };

    AudioPlayerCustom.prototype.onRateChange = function (rate) {
        this.updateSpeedUI(rate);
    };

    AudioPlayerCustom.prototype.onSeeked = function (curr) {
        this.updateCurrentTimeUI(curr);
        this.updateSeekBarValue(curr);
    };

    AudioPlayerCustom.prototype.bindChildControls = function () {
        var self = this;

        this.cacheControlDrops();

        // Bind event listeners on each cached control drop
        _.each(this.controlDropCache, function (childDrop, role) {
            if (!childDrop || !childDrop.$element) return;

            // Speed dropdown: populate options and bind change event
            if (role === "audio_speed" && childDrop.$dropDown) {
                childDrop.$dropDown.empty();
                var postfix = self.drop.getSpeedPostfix ? self.drop.getSpeedPostfix() : "x";
                _.each(self.playbackRates, function (rate) {
                    $("<option />", {
                        value: rate.toString(),
                        text: rate + postfix,
                    }).appendTo(childDrop.$dropDown);
                });

                var currentRate = self.playbackRates[self.currentRateIndex] || 1.0;
                childDrop.$dropDown.val(currentRate.toString());

                if (childDrop.dropKickObject && typeof childDrop.dropKickObject.refresh === "function") {
                    childDrop.dropKickObject.refresh();
                }

                childDrop.$dropDown.off(".audiocontrol").on("change.audiocontrol", function () {
                    var val = parseFloat($(this).val());
                    if (!isNaN(val) && val > 0) {
                        self.setPlaybackRate(val);
                    }
                });
                return;
            }

            // Seek bar slider: bind drag/slide events
            if (role === "audio_seek_bar") {
                var seekSliderInstance = self.getNoUiSlider(role);
                if (seekSliderInstance) {
                    seekSliderInstance.off(".audiocontrol");

                    seekSliderInstance.on("start.audiocontrol", function () {
                        self.isUserSliding = true;
                    });

                    seekSliderInstance.on("slide.audiocontrol", function () {
                        var val = Number(seekSliderInstance.get());
                        if (!isNaN(val)) {
                            if (self.audioElement) {
                                self.audioElement.currentTime = val;
                            }
                            self.updateCurrentTimeUI(val);
                        }
                    });

                    seekSliderInstance.on("change.audiocontrol end.audiocontrol", function () {
                        var val = Number(seekSliderInstance.get());
                        if (!isNaN(val)) {
                            if (self.audioElement) {
                                self.audioElement.currentTime = val;
                            }
                            self.updateCurrentTimeUI(val);
                            self.triggerEvent("seekChanged", val);
                        }
                        self.isUserSliding = false;
                    });
                    return;
                }
            }

            // Volume slider: bind drag/slide events (fire volumeChanged only on drag end/change)
            if (role === "audio_volume_slider") {
                var volSliderInstance = self.getNoUiSlider(role);
                if (volSliderInstance) {
                    volSliderInstance.off(".audiocontrol");

                    volSliderInstance.on("start.audiocontrol", function () {
                        self.isUserAdjustingVolume = true;
                    });

                    volSliderInstance.on("slide.audiocontrol", function () {
                        var val = Number(volSliderInstance.get());
                        if (!isNaN(val)) {
                            self.applyVolume(val);
                        }
                    });

                    volSliderInstance.on("change.audiocontrol end.audiocontrol", function () {
                        var val = Number(volSliderInstance.get());
                        if (!isNaN(val)) {
                            var displayVal = self.applyVolume(val);
                            if (displayVal !== null) {
                                self.triggerEvent("volumeChanged", displayVal);
                            }
                        }

                        self.isUserAdjustingVolume = false;
                    });
                    return;
                }
            }

            // Button controls: bind click events
            childDrop.$element.off(".audiocontrol");

            childDrop.$element.on("click.audiocontrol", function (e) {
                e.stopPropagation();

                switch (role) {
                    case "audio_play":
                        self.playAudio();
                        break;
                    case "audio_pause":
                        self.pauseAudio();
                        break;
                    case "audio_mute":
                        self.setMuted(true);
                        break;
                    case "audio_unmute":
                        self.setMuted(false);
                        break;
                    case "audio_speed":
                        self.cyclePlaybackRate();
                        break;
                    case "audio_skip_back":
                        self.skip(-self.getSkipTimeSeconds());
                        break;
                    case "audio_skip_forward":
                        self.skip(self.getSkipTimeSeconds());
                        break;
                }
            });
        });
    };

    AudioPlayerCustom.prototype.updateCurrentTimeUI = function (curr) {
        var formatted = this.drop.format(curr);
        var drop = this.getControlDrop("audio_current_time");
        if (drop && typeof drop.setValue === "function") {
            drop.setValue({ value: formatted, type: "string" });
        }
    };

    AudioPlayerCustom.prototype.updateDurationUI = function (dur) {
        var formatted = this.drop.format(dur);
        var drop = this.getControlDrop("audio_duration");
        if (drop && typeof drop.setValue === "function") {
            drop.setValue({ value: formatted, type: "string" });
        }
    };

    AudioPlayerCustom.prototype.updateSeekBarMax = function (maxVal) {
        var limit = maxVal && maxVal > 0 ? maxVal : 100;
        var slider = this.getNoUiSlider("audio_seek_bar");
        if (slider) {
            slider.updateOptions({
                range: {
                    min: 0,
                    max: limit,
                },
            });
        }
    };

    AudioPlayerCustom.prototype.updateSeekBarValue = function (val) {
        if (this.isUserSliding) return;

        var seekBar = this.getControlDrop("audio_seek_bar");
        if (seekBar && typeof seekBar.setValue === "function") {
            seekBar.setValue({ value: val });
        }
    };

    AudioPlayerCustom.prototype.updateVolumeSliderValue = function (val) {
        var volumeSlider = this.getControlDrop("audio_volume_slider");
        if (volumeSlider && typeof volumeSlider.setValue === "function") {
            volumeSlider.setValue({ value: val });
        }
    };

    AudioPlayerCustom.prototype.updateSpeedUI = function (rate) {
        var postfix = this.drop.getSpeedPostfix ? this.drop.getSpeedPostfix() : "x";
        var text = rate + postfix;
        var drop = this.getControlDrop("audio_speed");
        if (!drop) return;

        if (drop.$dropDown) {
            drop.$dropDown.val(rate.toString());
            if (typeof drop.setValue === "function") {
                drop.setValue({ value: rate.toString() });
            }
            if (drop.dropKickObject && typeof drop.dropKickObject.refresh === "function") {
                drop.dropKickObject.refresh();
            }
        } else if (typeof drop.setValue === "function") {
            drop.setValue({ value: text, type: "string" });
        } else if (drop.$text) {
            drop.$text.text(text);
        }
    };

    AudioPlayerCustom.prototype.updatePlayPauseUI = function (isPlaying) {
        var playDrop = this.getControlDrop("audio_play");
        var pauseDrop = this.getControlDrop("audio_pause");

        if (playDrop && playDrop.$element) playDrop.$element.toggle(!isPlaying);
        if (pauseDrop && pauseDrop.$element) pauseDrop.$element.toggle(!!isPlaying);
    };

    AudioPlayerCustom.prototype.updateMuteUI = function () {
        var isMuted = this.audioElement
            ? this.audioElement.muted || this.audioElement.volume === 0
            : this.drop.data
              ? !!this.drop.data.muted
              : false;
        var muteDrop = this.getControlDrop("audio_mute");
        var unmuteDrop = this.getControlDrop("audio_unmute");

        if (muteDrop && muteDrop.$element) muteDrop.$element.toggle(!isMuted);
        if (unmuteDrop && unmuteDrop.$element) unmuteDrop.$element.toggle(isMuted);
    };

    AudioPlayerCustom.prototype.startSeekAnimation = function () {
        var self = this;
        this.stopSeekAnimation();
        (function tick() {
            if (!self.audioElement || self.audioElement.paused) {
                self.rafId = null;
                return;
            }
            if (!self.isUserSliding) {
                self.updateSeekBarValue(self.audioElement.currentTime);
            }
            self.rafId = requestAnimationFrame(tick);
        })();
    };

    AudioPlayerCustom.prototype.stopSeekAnimation = function () {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    };

    AudioPlayerCustom.prototype.resetPlayerState = function (resetDuration) {
        AudioPlayerBase.prototype.resetPlayerState.call(this, resetDuration);

        var defaultRate = this.getDefaultPlaybackRate();
        this.isUserSliding = false;
        this.isUserAdjustingVolume = false;
        this.stopSeekAnimation();
        this.updatePlayPauseUI(false);

        this.updateCurrentTimeUI(0);
        this.updateSeekBarValue(0);

        this.updateSpeedUI(defaultRate);

        if (resetDuration !== false) {
            this.updateDurationUI(0);
            this.updateSeekBarMax(100);
        }
    };

    AudioPlayerCustom.prototype.dispose = function () {
        this.stopSeekAnimation();

        if (this.controlDropCache) {
            _.each(this.controlDropCache, function (childDrop) {
                if (!childDrop) return;
                if (childDrop.$element) {
                    childDrop.$element.off(".audiocontrol");
                }
                if (childDrop.$dropDown) {
                    childDrop.$dropDown.off(".audiocontrol");
                }
                if (childDrop.$slider && childDrop.$slider[0] && childDrop.$slider[0].noUiSlider) {
                    childDrop.$slider[0].noUiSlider.off(".audiocontrol");
                }
            });
            this.controlDropCache = null;
        }

        AudioPlayerBase.prototype.dispose.call(this);
    };

    return AudioPlayerCustom;
});

